import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Firestore, doc, updateDoc } from 'firebase/firestore';

import {
  getStreamUploadUrl,
  StreamServiceError,
  StreamUploadAbortedError,
  uploadVideoToStream,
} from '../services/streamService';
import { VideoScoreValue } from '../types/athlete';

export type VideoUploadStatus = 'uploading' | 'done' | 'error';

export interface VideoUploadEntry {
  key: string;
  status: VideoUploadStatus;
  progress: number;
  videoUid?: string;
  errorMessage?: string;
  athleteId: string;
  athleteName: string;
}

interface StartUploadParams {
  key: string;
  blob: Blob;
  mimeType: string;
  coachUid: string;
  athleteName: string;
  /** Firestore writer config — provider writes the videoUid itself once upload completes. */
  firestore: {
    db: Firestore;
    tryoutId: string;
    athleteId: string;
    fieldId: string;
  };
}

interface VideoUploadContextValue {
  entries: VideoUploadEntry[];
  getEntry: (key: string) => VideoUploadEntry | undefined;
  startUpload: (params: StartUploadParams) => void;
  /** Removes a finished entry from the list (used to clear errors after acknowledgement). */
  clearEntry: (key: string) => void;
}

const VideoUploadContext = createContext<VideoUploadContextValue | null>(null);

interface UploadInternal {
  entry: VideoUploadEntry;
  abortController?: AbortController;
  /**
   * Bumped each time `startUpload` is called for this key. Async callbacks
   * compare against the run id they were started under so that an abandoned
   * upload (replaced by a newer call to `startUpload` for the same key)
   * doesn't write to the state of the new run.
   */
  runId: number;
}

export const VideoUploadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [entries, setEntries] = useState<Record<string, VideoUploadEntry>>({});
  const internalRef = useRef<Record<string, UploadInternal>>({});

  const updateEntry = useCallback((key: string, patch: Partial<VideoUploadEntry>): void => {
    setEntries((prev) => {
      const existing = prev[key];
      if (!existing) return prev;
      const next: VideoUploadEntry = { ...existing, ...patch };
      internalRef.current[key] = {
        ...internalRef.current[key],
        entry: next,
      };
      return { ...prev, [key]: next };
    });
  }, []);

  const removeEntry = useCallback((key: string): void => {
    setEntries((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    delete internalRef.current[key];
  }, []);

  const startUpload = useCallback((params: StartUploadParams): void => {
    const { key, blob, mimeType, coachUid, athleteName, firestore } = params;

    // Abort any in-flight upload reusing this key so its progress callbacks
    // don't bleed into the new entry. The run id below makes any straggling
    // async callbacks no-op even if the abort wasn't honored.
    const previous = internalRef.current[key];
    previous?.abortController?.abort();
    const runId = (previous?.runId ?? 0) + 1;
    const abortController = new AbortController();

    const initial: VideoUploadEntry = {
      key,
      status: 'uploading',
      progress: 0,
      athleteId: firestore.athleteId,
      athleteName,
    };
    internalRef.current[key] = { entry: initial, runId, abortController };
    setEntries((prev) => ({ ...prev, [key]: initial }));

    const isCurrent = (): boolean => internalRef.current[key]?.runId === runId;

    const run = async (): Promise<void> => {
      try {
        const { uploadUrl, uid } = await getStreamUploadUrl();
        if (!isCurrent()) return;

        await uploadVideoToStream(
          uploadUrl,
          blob,
          mimeType,
          (pct) => {
            if (isCurrent()) updateEntry(key, { progress: pct });
          },
          abortController.signal,
        );
        if (!isCurrent()) return;

        const value: VideoScoreValue = { videoUid: uid, uploadedBy: coachUid };
        const athleteRef = doc(
          firestore.db,
          'tryouts',
          firestore.tryoutId,
          'athletes',
          firestore.athleteId,
        );
        await updateDoc(athleteRef, {
          [`scores.${firestore.fieldId}.video`]: value,
        });
        if (!isCurrent()) return;

        updateEntry(key, { status: 'done', progress: 100, videoUid: uid });
        // Done entries auto-clear after a short window so the indicator settles.
        window.setTimeout(() => {
          if (isCurrent()) removeEntry(key);
        }, 1500);
      } catch (err) {
        if (!isCurrent()) return;
        if (err instanceof StreamUploadAbortedError) return;
        const message = err instanceof StreamServiceError
          ? err.message
          : 'Upload failed. Please try again.';
        updateEntry(key, { status: 'error', errorMessage: message });
      }
    };

    void run();
  }, [updateEntry, removeEntry]);

  const getEntry = useCallback(
    (key: string): VideoUploadEntry | undefined => entries[key],
    [entries],
  );

  const clearEntry = useCallback((key: string): void => {
    removeEntry(key);
  }, [removeEntry]);

  // Warn before unload (refresh / close tab) when uploads are still in flight.
  useEffect(() => {
    const hasUploading = Object.values(entries).some((e) => e.status === 'uploading');
    if (!hasUploading) return;
    const handler = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [entries]);

  const value = useMemo<VideoUploadContextValue>(() => ({
    entries: Object.values(entries),
    getEntry,
    startUpload,
    clearEntry,
  }), [entries, getEntry, startUpload, clearEntry]);

  return (
    <VideoUploadContext.Provider value={value}>
      {children}
    </VideoUploadContext.Provider>
  );
};

export function useVideoUploads(): VideoUploadContextValue {
  const ctx = useContext(VideoUploadContext);
  if (!ctx) throw new Error('useVideoUploads must be used within VideoUploadProvider');
  return ctx;
}
